/**
 * Utility functions for navigating between course items
 */

import { normalizeId } from './id';

export interface NextItemResult {
  type: 'item' | 'rating' | 'summary' | 'course-end';
  courseId: string;
  chapterId: string;
  itemId?: string;
  itemType?: 'capsule' | 'quiz' | 'workshop';
  nextChapterId?: string;
}

/**
 * Find the next item to navigate to after completing the current item
 */
export const getNextItem = (
  course: any,
  currentChapterId: string,
  currentItemId: string
): NextItemResult | null => {
  if (!course || !course.chapters) return null;

  const currentChapter = course.chapters.find((ch: any) => ch._id === currentChapterId);
  if (!currentChapter || !currentChapter.items) return null;

  // Sort items by order
  const sortedItems = [...currentChapter.items].sort((a: any, b: any) => a.order - b.order);
  
  // Find current item index
  const currentIndex = sortedItems.findIndex((item: any) => (item?._id?.toString?.() || item?._id || item?.id) === currentItemId);
  
  if (currentIndex === -1) return null;

  // Check if there's a next item in the same chapter
  if (currentIndex < sortedItems.length - 1) {
    const nextItem = sortedItems[currentIndex + 1];
    return {
      type: 'item',
      courseId: course._id || course.id,
      chapterId: currentChapterId,
      itemId: nextItem?._id?.toString?.() || nextItem?._id || nextItem?.id,
      itemType: nextItem.itemType
    };
  }

  // This is the last item of the chapter, go to rating page
  // Prochain chapitre par ordre (y compris verrouillé — pour l'aperçu en fin de chapitre).
  const currentChapterOrder = Number(currentChapter.order || 0);
  const nextChapter = [...course.chapters]
    .sort((a: any, b: any) => Number(a.order || 0) - Number(b.order || 0))
    .find((ch: any) => Number(ch.order || 0) > currentChapterOrder);
  
  const requiresRating = currentChapter.requiresRating !== false;

  return {
    type: requiresRating ? 'rating' : 'summary',
    courseId: course._id || course.id,
    chapterId: currentChapterId,
    nextChapterId: nextChapter?._id || nextChapter?.id
  };
};

/**
 * Get the URL path for the next item
 */
export const getNextItemPath = (nextItem: NextItemResult | null): string | null => {
  if (!nextItem) return null;

  const { courseId, chapterId, type, itemId, itemType, nextChapterId } = nextItem;

  switch (type) {
    case 'item':
      if (!itemId || !itemType) return null;
      return `/course/${courseId}/chapter/${chapterId}/${itemType}/${itemId}`;
    
    case 'rating':
      return `/course/${courseId}/chapter/${chapterId}/rating${nextChapterId ? `?next=${nextChapterId}` : ''}`;
    
    case 'summary':
      return `/course/${courseId}/chapter/${chapterId}/summary${nextChapterId ? `?next=${nextChapterId}` : ''}`;
    
    case 'course-end':
      return `/course/${courseId}`;
    
    default:
      return null;
  }
};

/**
 * Premier module du parcours : premier chapitre (order === 1 ou ordre minimal),
 * premier item par ordre. Aligné sur CourseDetail.handleResume quand la progression est à 0.
 */
export const getFirstModulePath = (courseId: string, course: any): string | null => {
  if (!course?.chapters?.length) return null;

  const firstChapter =
    course.chapters.find((ch: any) => ch.order === 1) ??
    [...course.chapters].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))[0];

  if (!firstChapter?.items?.length) return null;

  const sortedItems = [...firstChapter.items].sort(
    (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)
  );
  const firstItem = sortedItems[0];
  const firstItemId = normalizeId(firstItem?._id || firstItem?.id);
  const chapterId = normalizeId(firstChapter._id);
  if (!firstItemId || !chapterId) return null;

  const itemPath =
    firstItem.itemType === 'quiz'
      ? `quiz/${firstItemId}`
      : firstItem.itemType === 'workshop'
      ? `workshop/${firstItemId}`
      : `capsule/${firstItemId}`;

  return `/course/${courseId}/chapter/${chapterId}/${itemPath}`;
};

